import { http } from './client'
import type {
  Cart,
  CancelReservationResult,
  CheckCouponResult,
  ConfirmOnlineResult,
  ConfirmOnsiteResult,
  BookingInfo,
  CalculateAmount,
  PaymentConfig,
  PayPalConfig,
  ReservationOptionSettings,
  ReservationSettings,
  TransportContext,
} from './types'

const enc = encodeURIComponent

/** Bootstrap: active transports with their booking windows. */
export function getMainContext() {
  return http.get<TransportContext[]>('/api/configuratorMainContext')
}

/** Rates + base calculation for a transport / date / time / end-day. */
export function getReservationSettings(
  transportId: number,
  day: string,
  hour: string,
  endDay: string,
) {
  return http.get<ReservationSettings>(
    `/api/reservationSettings/${transportId}/${enc(day)}/${enc(hour)}/${enc(endDay)}`,
  )
}

/** Services + conventions + coupon applicability for the current cart. */
export function getOptionSettings(cart: Cart) {
  return http.post<ReservationOptionSettings>('/api/reservationOptionSettings/', cart)
}

/** Live price for the current cart. */
export function calculateAmount(cart: Cart) {
  return http.post<CalculateAmount>('/api/calculateAmount/', cart)
}

/** Validate a coupon code. */
export function checkCoupon(transportId: number, day: string, code: string) {
  return http.post<CheckCouponResult>('/api/checkCoupon/', { transportId, day, code })
}

/** Available payment modes for a reservation day / transport. */
export function getPaymentConfig(reservationDay: string, transportId: number) {
  return http.get<PaymentConfig>(`/api/paymentConfig/${enc(reservationDay)}/${enc(String(transportId))}`)
}

/**
 * Public PayPal SDK config. Prefers the build-time env (client-id is public and lives in the
 * SDK url anyway) so the deployed app doesn't depend on the backend exposing /api/paypalConfig/;
 * falls back to the backend endpoint when the env isn't set (e.g. local dev).
 */
export function getPayPalConfig(): Promise<PayPalConfig> {
  const envClient = import.meta.env.VITE_PAYPAL_CLIENT_ID as string | undefined
  if (envClient) {
    return Promise.resolve({
      clientId: envClient,
      currency: 'EUR',
      intent: 'capture',
      environment: ((import.meta.env.VITE_PAYPAL_ENV as string) || 'live') === 'sandbox' ? 'sandbox' : 'live',
    })
  }
  return http.get<PayPalConfig>('/api/paypalConfig/')
}

/** Confirm a reservation (ONSITE → confirmed; ONLINE → draft + paymentId). */
export function confirmReservation(payload: {
  cart: Cart
  bookingInfo: BookingInfo
  calculatedInfo: CalculateAmount
  paymentResult: unknown
}) {
  return http.post<ConfirmOnsiteResult & ConfirmOnlineResult>('/api/confirmReservation/', payload)
}

/** Capture a PayPal order and finalize the draft reservation. */
export function captureReservation(payload: {
  reservationCode: string
  paymentResult: unknown
  paymentId: string
  __captchaToken?: string
}) {
  return http.post<ConfirmOnsiteResult>('/api/captureReservation/', payload)
}

/**
 * Cancel a confirmed reservation. Needs the booking code + its cancellation
 * token (both present in the bookings list). Allowed only until the day before
 * arrival; no automatic refund — staff handle online refunds manually.
 */
export function cancelReservation(reservationCode: string, cancellationCode: string) {
  return http.post<CancelReservationResult>('/api/cancelReservation/', { reservationCode, cancellationCode })
}
